import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  Box, 
  TextField, 
  Button, 
  Divider, 
  Typography, 
  Snackbar, 
  Alert,
  IconButton,
  InputAdornment,
  Paper,
  Tooltip
} from '@mui/material';
import { Title, useDataProvider, useNotify } from 'react-admin';
import LockIcon from '@mui/icons-material/Lock';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const UserSettingsPage = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  const dataProvider = useDataProvider();
  const notify = useNotify();
  
  // Get user data from localStorage
  const userId = localStorage.getItem('userId');
  const username = localStorage.getItem('username');
  const userRole = localStorage.getItem('userRole');
  
  // Function to generate a random password
  const generateRandomPassword = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()-_=+";
    let password = '';
    
    // Generate a 20-character password
    for (let i = 0; i < 20; i++) {
      const randomIndex = Math.floor(Math.random() * chars.length);
      password += chars[randomIndex];
    }
    
    setNewPassword(password);
    setConfirmPassword(password);
    
    // Show both passwords
    setShowNewPassword(true);
    setShowConfirmPassword(true);
    
    // Show success message
    setSnackbarMessage('Random password generated! Be sure to save it somewhere secure.');
    setSnackbarSeverity('info');
    setOpenSnackbar(true);
  };
  
  // Function to copy password to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(newPassword);
    notify('Password copied to clipboard', { type: 'success' });
  };
  
  // Handle password change form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset error state
    setPasswordError('');
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    // Validate password strength (at least 8 characters)
    if (newPassword.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return;
    }
    
    try {
      // Update password in the API
      await dataProvider.update('users', {
        id: userId,
        data: {
          username: username,  // Keep the same username
          password: newPassword,
          currentPassword: currentPassword
        }
      });
      
      // Clear input fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      // Show success message
      setSnackbarMessage('Password updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // Also show notification
      notify('Password updated successfully', { type: 'success' });
    } catch (error) {
      console.error('Error updating password:', error);
      
      // Show error message
      setSnackbarMessage(error.message || 'Failed to update password. Please try again.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      
      // Also show notification
      notify(error.message || 'Password update failed', { type: 'error' });
    }
  };
  
  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  
  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', p: 2 }}>
      <Title title="User Settings" />
      
      <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <AccountCircleIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
          <Typography variant="h5">Account Information</Typography>
        </Box>
        <Divider sx={{ mb: 3 }} />
        
        <Typography variant="subtitle1">Username: <strong>{username}</strong></Typography>
        <Typography variant="subtitle1">Role: <strong>{userRole === 'tenant_admin' ? 'Tenant Admin' : 'Team Admin'}</strong></Typography>
      </Paper>
      
      <Card sx={{ borderRadius: 2 }}>
        <CardHeader 
          title={
            <Box display="flex" alignItems="center">
              <LockIcon sx={{ mr: 1 }} />
              <Typography variant="h5">Change Password</Typography>
            </Box>
          } 
        />
        <Divider />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column' }}>
            <TextField
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              type={showCurrentPassword ? 'text' : 'password'}
              margin="normal"
              variant="outlined"
              required
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 1 }}>
              <TextField
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type={showNewPassword ? 'text' : 'password'}
                margin="normal"
                variant="outlined"
                required
                fullWidth
                error={!!passwordError}
                helperText={passwordError}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex' }}>
                        <Tooltip title="Generate random password">
                          <IconButton
                            onClick={generateRandomPassword}
                            edge="end"
                            color="primary"
                          >
                            <AutorenewIcon />
                          </IconButton>
                        </Tooltip>
                        <IconButton
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          edge="end"
                        >
                          {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            {newPassword && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                p: 1, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                mb: 2
              }}>
                <Box sx={{ 
                  flexGrow: 1, 
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}>
                  {newPassword}
                </Box>
                <IconButton
                  size="small"
                  onClick={copyToClipboard}
                  sx={{ ml: 1 }}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
            
            <TextField
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              type={showConfirmPassword ? 'text' : 'password'}
              margin="normal"
              variant="outlined"
              required
              fullWidth
              error={!!passwordError}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Button
                type="button"
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
              >
                Reset Form
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!currentPassword || !newPassword || !confirmPassword}
              >
                Update Password
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      
      <Snackbar 
        open={openSnackbar} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserSettingsPage;